import type {
  Get_Ad_ProductQuery,
  Get_Ad_TypesQuery,
  Get_CountriesQuery,
  Get_Creative_Review_StatusesQuery,
  Get_Creative_TypesQuery,
  Get_Currency_TypesQuery,
  Get_Distribution_PartnersQuery,
  Get_Line_Item_Billable_Third_PartiesQuery,
  Get_Line_Item_Tier_NamesQuery,
  Get_Line_Priority_ValuesQuery,
  Get_Order_TypesQuery,
  Get_Publisher_List_V4Query,
} from '../../../../apis/graphql';
import { DropdownOptions, MultiDropdownOptions } from '../../../../common/hooks/config';
import { usePredefinedDropdownOptions } from '../../../../common/hooks/filters/usePredefinedDropdownOptions';
import { useFetchDropdownOptions } from '../../../../common/hooks/queries/useFetchDropdownOptions';
import type { MultiDropdownQueryType } from '../../../../common/hooks/queries/useFetchMultiDropdownOptions';
import { useFetchMultiDropdownOptions } from '../../../../common/hooks/queries/useFetchMultiDropdownOptions';
import { TRAFFICKING_SEARCH_PARAMS } from '../../../../constants';
import { DEFAULT_RANGE_FILTER_VALUE } from '../../../../contexts/FilterContext';
import type { AdditionalFilterInDropdown, UseOptions } from './constants';
import {
  AdditionalFilterType,
  ComparisonFilterSubType,
  DEFAULT_AGENCY_DROPDOWN_BUTTON_PLACEHOLDER,
  DEFAULT_AGENCY_SEARCH_INPUT_PLACEHOLDER,
  DEFAULT_BOOLEAN_OPTIONS,
  DEFAULT_BRAND_DROPDOWN_BUTTON_PLACEHOLDER,
  DEFAULT_BRAND_SEARCH_INPUT_PLACEHOLDER,
  DEFAULT_INDUSTRY_DROPDOWN_BUTTON_PLACEHOLDER,
  DEFAULT_INDUSTRY_SEARCH_INPUT_PLACEHOLDER,
  DO_NOT_TRAFFIC_BOOLEAN_OPTIONS,
} from './constants';
import {
  getCPMValueToDisplay,
  getCPMValueToSave,
  getPercentPacingValueToDisplay,
  getPercentPacingValueToSave,
} from './utils';

/**
 * Filters such as Pacing Status, Line Item Status, and Ad Status have an exact, fixed order and always come first of other filters
 * A new filter could be added in a particular, fixed position based on the requirements
 */
export const filtersWithFixedOrderConfig: AdditionalFilterInDropdown[] = [
  {
    type: AdditionalFilterType.CHECKBOX,
    label: 'Pacing Status',
    multiOptionalLabel: 'All Pacing statuses',
    searchParam: TRAFFICKING_SEARCH_PARAMS.GENERAL_PACING_STATUS_LIST,
    value: [],
    useOptions: (): UseOptions => usePredefinedDropdownOptions(TRAFFICKING_SEARCH_PARAMS.GENERAL_PACING_STATUS_LIST),
  },
  {
    type: AdditionalFilterType.CHECKBOX,
    label: 'Line Item Status',
    multiOptionalLabel: 'All Line Item statuses',
    searchParam: TRAFFICKING_SEARCH_PARAMS.LINE_ITEM_STATUS_LIST,
    value: [],
    useOptions: (): UseOptions => usePredefinedDropdownOptions(TRAFFICKING_SEARCH_PARAMS.LINE_ITEM_STATUS_LIST),
  },
  {
    type: AdditionalFilterType.RADIOBUTTON,
    label: 'Ad Status',
    multiOptionalLabel: 'All Ad Statuses',
    searchParam: TRAFFICKING_SEARCH_PARAMS.AD_STATUS_LIST,
    value: [],
    useOptions: (): UseOptions => usePredefinedDropdownOptions(TRAFFICKING_SEARCH_PARAMS.AD_STATUS_LIST),
  },
];

/**
 * A new filter with dynamic order could be added to the end of the config
 */
export const filtersWithDynamicOrderConfig: AdditionalFilterInDropdown[] = [
  {
    type: AdditionalFilterType.CHECKBOX,
    label: 'Review Status',
    multiOptionalLabel: 'All Review Statuses',
    searchParam: TRAFFICKING_SEARCH_PARAMS.REVIEW_STATUS_LIST,
    value: [],
    useOptions: (): UseOptions => usePredefinedDropdownOptions(TRAFFICKING_SEARCH_PARAMS.REVIEW_STATUS_LIST),
  },
  {
    type: AdditionalFilterType.CHECKBOX,
    label: 'Order Type',
    multiOptionalLabel: 'All Order types',
    searchParam: TRAFFICKING_SEARCH_PARAMS.ORDER_TYPE_LIST,
    value: [],
    useOptions: (): UseOptions => useFetchDropdownOptions<Get_Order_TypesQuery>(DropdownOptions.OrderTypes),
  },
  {
    type: AdditionalFilterType.DROPDOWN,
    label: 'Brand',
    multiOptionalLabel: 'All Brands',
    searchParam: TRAFFICKING_SEARCH_PARAMS.BRAND_ID_LIST,
    value: [],
    includeExcludeOptions: {
      include: TRAFFICKING_SEARCH_PARAMS.BRAND_ID_LIST,
      exclude: TRAFFICKING_SEARCH_PARAMS.EXCLUDE_BRAND_ID_LIST,
    },
    multiDropdownPlaceholder: {
      dropdownButtonPlaceholder: DEFAULT_BRAND_DROPDOWN_BUTTON_PLACEHOLDER,
      searchInputPlaceholder: DEFAULT_BRAND_SEARCH_INPUT_PLACEHOLDER,
    },
    useOptions: (): UseOptions => useFetchMultiDropdownOptions<MultiDropdownQueryType>(MultiDropdownOptions.Brands),
  },
  {
    type: AdditionalFilterType.DROPDOWN,
    label: 'Agency',
    multiOptionalLabel: 'All Agencies',
    searchParam: TRAFFICKING_SEARCH_PARAMS.AGENCY_ID_LIST,
    value: [],
    multiDropdownPlaceholder: {
      dropdownButtonPlaceholder: DEFAULT_AGENCY_DROPDOWN_BUTTON_PLACEHOLDER,
      searchInputPlaceholder: DEFAULT_AGENCY_SEARCH_INPUT_PLACEHOLDER,
    },
    useOptions: (): UseOptions => useFetchMultiDropdownOptions<MultiDropdownQueryType>(MultiDropdownOptions.Agencies),
  },
  {
    type: AdditionalFilterType.DROPDOWN,
    label: 'Industry',
    multiOptionalLabel: 'All Industries',
    searchParam: TRAFFICKING_SEARCH_PARAMS.INDUSTRY_ID_LIST,
    value: [],
    multiDropdownPlaceholder: {
      dropdownButtonPlaceholder: DEFAULT_INDUSTRY_DROPDOWN_BUTTON_PLACEHOLDER,
      searchInputPlaceholder: DEFAULT_INDUSTRY_SEARCH_INPUT_PLACEHOLDER,
    },
    useOptions: (): UseOptions => useFetchMultiDropdownOptions<MultiDropdownQueryType>(MultiDropdownOptions.Industries),
  },
  {
    type: AdditionalFilterType.CHECKBOX,
    label: 'Product Type',
    multiOptionalLabel: 'All Product Types',
    searchParam: TRAFFICKING_SEARCH_PARAMS.PRODUCT_TYPE,
    value: [],
    useOptions: (): UseOptions => useFetchDropdownOptions<Get_Ad_ProductQuery>(DropdownOptions.ProductType),
  },
  {
    type: AdditionalFilterType.CHECKBOX,
    label: 'Currency',
    multiOptionalLabel: 'All Currency Types',
    searchParam: TRAFFICKING_SEARCH_PARAMS.CURRENCY_LIST,
    value: [],
    useOptions: (): UseOptions => useFetchDropdownOptions<Get_Currency_TypesQuery>(DropdownOptions.CurrencyList),
  },
  {
    type: AdditionalFilterType.CHECKBOX,
    label: 'Billable 3p',
    multiOptionalLabel: 'All Billable 3ps',
    searchParam: TRAFFICKING_SEARCH_PARAMS.BILLABLE_THIRD_PARTY_LIST,
    value: [],
    useOptions: (): UseOptions =>
      useFetchDropdownOptions<Get_Line_Item_Billable_Third_PartiesQuery>(DropdownOptions.LineItemBillableThirdParties),
  },
  {
    type: AdditionalFilterType.CHECKBOX,
    label: 'Priority Value',
    multiOptionalLabel: 'Priority Values',
    searchParam: TRAFFICKING_SEARCH_PARAMS.PRIORITY_VALUE_LIST,
    value: [],
    useOptions: (): UseOptions =>
      useFetchDropdownOptions<Get_Line_Priority_ValuesQuery>(DropdownOptions.LineItemPriorityValues),
  },
  {
    type: AdditionalFilterType.CHECKBOX,
    label: 'Tier Name',
    multiOptionalLabel: 'Tier Names',
    searchParam: TRAFFICKING_SEARCH_PARAMS.TIER_NAME_LIST,
    value: [],
    useOptions: (): UseOptions =>
      useFetchDropdownOptions<Get_Line_Item_Tier_NamesQuery>(DropdownOptions.LineItemTierNames),
  },
  {
    type: AdditionalFilterType.CHECKBOX,
    label: 'Publisher',
    multiOptionalLabel: 'Publishers',
    searchParam: TRAFFICKING_SEARCH_PARAMS.PUBLISHER_LIST,
    value: [],
    useOptions: (): UseOptions => useFetchDropdownOptions<Get_Publisher_List_V4Query>(DropdownOptions.PublisherList),
  },
  {
    type: AdditionalFilterType.BOOLEAN,
    label: 'COPPA',
    searchParam: TRAFFICKING_SEARCH_PARAMS.IS_COPPA_OR_CARRU,
    value: null,
    options: DEFAULT_BOOLEAN_OPTIONS,
  },
  {
    type: AdditionalFilterType.BOOLEAN,
    label: 'Is Added Value',
    searchParam: TRAFFICKING_SEARCH_PARAMS.IS_ADDED_VALUE,
    value: null,
    options: DEFAULT_BOOLEAN_OPTIONS,
  },
  {
    type: AdditionalFilterType.BOOLEAN,
    label: 'Is Makegood',
    searchParam: TRAFFICKING_SEARCH_PARAMS.IS_MAKEGOOD,
    value: null,
    options: DEFAULT_BOOLEAN_OPTIONS,
  },
  {
    type: AdditionalFilterType.CHECKBOX,
    label: 'Ad Type',
    multiOptionalLabel: 'Ad Types',
    searchParam: TRAFFICKING_SEARCH_PARAMS.AD_TYPE_LIST,
    value: [],
    useOptions: (): UseOptions => useFetchDropdownOptions<Get_Ad_TypesQuery>(DropdownOptions.AdType),
  },
  {
    type: AdditionalFilterType.CHECKBOX,
    label: 'Creative Type',
    multiOptionalLabel: 'Creative Types',
    searchParam: TRAFFICKING_SEARCH_PARAMS.CREATIVE_TYPE_LIST,
    value: [],
    useOptions: (): UseOptions => useFetchDropdownOptions<Get_Creative_TypesQuery>(DropdownOptions.CreativeType),
  },
  {
    type: AdditionalFilterType.CHECKBOX,
    label: 'Creative Review Status',
    multiOptionalLabel: 'Creative Review Statuses',
    searchParam: TRAFFICKING_SEARCH_PARAMS.CREATIVE_REVIEW_LIST,
    value: [],
    useOptions: (): UseOptions =>
      useFetchDropdownOptions<Get_Creative_Review_StatusesQuery>(DropdownOptions.CreativeReviewStatus),
  },
  {
    type: AdditionalFilterType.BOOLEAN,
    label: 'Ad Rotation',
    searchParam: TRAFFICKING_SEARCH_PARAMS.IS_ROTATION,
    value: null,
    options: DEFAULT_BOOLEAN_OPTIONS,
  },
  {
    type: AdditionalFilterType.BOOLEAN,
    label: 'Ad Sequence',
    searchParam: TRAFFICKING_SEARCH_PARAMS.IS_SEQUENCE,
    value: null,
    options: DEFAULT_BOOLEAN_OPTIONS,
  },
  {
    type: AdditionalFilterType.CHECKBOX,
    label: 'Country',
    multiOptionalLabel: 'All countries',
    searchParam: TRAFFICKING_SEARCH_PARAMS.COUNTRY_LIST,
    value: [],
    useOptions: (): UseOptions => useFetchDropdownOptions<Get_CountriesQuery>(DropdownOptions.Countries),
  },
  {
    type: AdditionalFilterType.BOOLEAN,
    label: 'Do Not Traffic',
    searchParam: TRAFFICKING_SEARCH_PARAMS.DO_NOT_TRAFFIC,
    value: null,
    options: DO_NOT_TRAFFIC_BOOLEAN_OPTIONS,
  },
  {
    type: AdditionalFilterType.BOOLEAN,
    label: 'Frequency Cap',
    searchParam: TRAFFICKING_SEARCH_PARAMS.FREQUENCY_CAP,
    value: null,
    options: DEFAULT_BOOLEAN_OPTIONS,
  },
  {
    type: AdditionalFilterType.CHECKBOX,
    label: 'Pacing Type',
    multiOptionalLabel: 'All Pacing types',
    searchParam: TRAFFICKING_SEARCH_PARAMS.LINE_ITEM_TYPE_LIST,
    value: [],
    useOptions: (): UseOptions => usePredefinedDropdownOptions(TRAFFICKING_SEARCH_PARAMS.LINE_ITEM_TYPE_LIST),
  },
  {
    searchParam: TRAFFICKING_SEARCH_PARAMS.PERCENT_PACING,
    type: AdditionalFilterType.RANGE,
    label: '% Pacing',
    rangeOptions: {
      minPossibleValue: 0,
      maxPossibleValue: 1000,
      isPercentageValue: true,
      convertToDisplayValue: getPercentPacingValueToDisplay,
      convertToSaveValue: getPercentPacingValueToSave,
    },
    helperText: 'The values can be set in the range from 0 to 1000%.',
    value: DEFAULT_RANGE_FILTER_VALUE,
  },
  {
    searchParam: TRAFFICKING_SEARCH_PARAMS.COMPLETIONS_REMAINING,
    type: AdditionalFilterType.COMPARISON,
    subType: ComparisonFilterSubType.COMPLETIONS_REMAINING,
    label: 'Delivery Remaining',
    value: DEFAULT_RANGE_FILTER_VALUE,
  },
  {
    searchParam: TRAFFICKING_SEARCH_PARAMS.LINE_ITEM_END_DATE,
    type: AdditionalFilterType.COMPARISON,
    subType: ComparisonFilterSubType.LINE_ITEM_END_DATE,
    label: 'Line Item End Date',
    value: null,
  },
  {
    type: AdditionalFilterType.BOOLEAN,
    label: 'CP Applied',
    searchParam: TRAFFICKING_SEARCH_PARAMS.CUSTOM_PACING_APPLIED,
    value: null,
    options: DEFAULT_BOOLEAN_OPTIONS,
  },
  {
    type: AdditionalFilterType.CHECKBOX,
    label: 'Cost Method',
    multiOptionalLabel: 'All Cost Methods',
    searchParam: TRAFFICKING_SEARCH_PARAMS.COST_METHOD_LIST,
    value: [],
    useOptions: (): UseOptions => usePredefinedDropdownOptions(TRAFFICKING_SEARCH_PARAMS.COST_METHOD_LIST),
  },
  {
    type: AdditionalFilterType.CHECKBOX,
    label: 'SSP',
    multiOptionalLabel: 'All SSPs',
    searchParam: TRAFFICKING_SEARCH_PARAMS.SSP_LIST,
    value: [],
    useOptions: (): UseOptions => usePredefinedDropdownOptions(TRAFFICKING_SEARCH_PARAMS.SSP_LIST),
  },
  {
    type: AdditionalFilterType.CHECKBOX,
    label: 'Billable Event',
    multiOptionalLabel: 'All Billable Events',
    searchParam: TRAFFICKING_SEARCH_PARAMS.BILLABLE_EVENT_LIST,
    value: [],
    useOptions: (): UseOptions => usePredefinedDropdownOptions(TRAFFICKING_SEARCH_PARAMS.BILLABLE_EVENT_LIST),
  },
  {
    type: AdditionalFilterType.CHECKBOX,
    label: 'Distribution Partner',
    multiOptionalLabel: 'All Distribution Partners',
    searchParam: TRAFFICKING_SEARCH_PARAMS.DISTRIBUTION_PARTNER_LIST,
    value: [],
    useOptions: (): UseOptions =>
      useFetchDropdownOptions<Get_Distribution_PartnersQuery>(DropdownOptions.DistributionPartnerList),
  },
  {
    searchParam: TRAFFICKING_SEARCH_PARAMS.CPM,
    type: AdditionalFilterType.RANGE,
    label: 'Net CPM',
    rangeOptions: {
      minPossibleValue: 0,
      maxPossibleValue: 10000000,
      convertToDisplayValue: getCPMValueToDisplay,
      convertToSaveValue: getCPMValueToSave,
    },
    helperText: 'The values can be set in the range from 0 to 100000000.',
    value: DEFAULT_RANGE_FILTER_VALUE,
  },
];
